/* eslint-disable */
import { QueryParams } from './webform_query_params';
import { EntityType, entityTypeFromJSON, entityTypeToJSON } from './inspector_gadget';


/**
 *  This is only used for the front-end. In Tintin, there's a GetUserDataRequest.java
 *  that is used to parse the payload.
 */
export interface GetUserDataRequest {
  username: string | undefined;
  email: string | undefined;
  reportOrigin?: ReportOriginType | undefined;
}

export interface GetUserDataTypeResponse {
  emailType?: EmailType | undefined;
  country?: string | undefined;
}

export interface GetEmailTypeRequest {
  username: string;
}

export interface GetEmailTypeResponse {
  emailType: EmailType;
}

/**
 *  Represents a request to send a verification email to the specified email address.
 *  The corresponding response is GetCodeResponse.
 */
export interface GetCodeRequest {
  /**
   *  The email address to send the verification code to.
   */
  email: string;
}

/**
 *  Represents a response after a sent verification email.
 */
export interface GetCodeResponse {
  /**
   *  The detected type of the email.
   */
  emailType: EmailType;
  /**
   *  The UUID of the verification resource. Needs to be sent in ValidateCodeRequest.
   */
  resourceUri: string;
}

/**
 *  Represents a request to validate a code.
 *  The corresponding response does not have a payload and uses HTTP status codes only.
 */
export interface ValidateCodeRequest {
  /**
   *  The email address that was used to request the code.
   */
  email: string;
  /**
   *  The UUID of the verification resource, as specified in the preceding GetCodeResponse.
   */
  resourceUri: string;
  /**
   *  The verification code to be validated.
   */
  code: string;
}

/**
 *  Represents a request by a non-Spotify employee to submit a Content Policy report.
 *  The corresponding response does not have a payload and uses HTTP status codes only.
 */
export interface ExternalContentPolicyReportRequest {
  /**
   *  The details to verify that the user went through all the steps of the form.
   */
  verificationDetails: VerificationDetails | undefined;
  /**
   *  The report data that captures what the user selected/entered on the form.
   */
  report: ExternalContentPolicyReport | undefined;
}

/**
 *  Represents a request by a Spotify employee to submit a Content Policy report.
 *  The corresponding response does not have a payload and uses HTTP status codes only.
 */
export interface EmployeeContentPolicyReportRequest {
  /**
   *  The details to verify that the user went through all the steps of the form.
   */
  verificationDetails: VerificationDetails | undefined;
  /**
   *  The report data that captures what the user selected/entered on the form.
   */
  report: EmployeeContentPolicyReport | undefined;
}

export interface Experiment {
  name: string;
  group: string;
}

export interface VerificationDetails {
  /**
   *  The UUID of the verification resource, as specified in the preceding GetCodeResponse.
   */
  resourceUri?: string | undefined;
  /**
   *  The verification code to be validated.
   */
  code?: string | undefined;
  googleRecaptchaToken: string;
}

export interface ExternalContentPolicyReport {
  /**
   *  The email of the reporter. Needs to be the same email as validated on the first step.
   */
  reporterEmail: string;
  /**
   *  The two character country code of the reporter. E.g. 'SE' or 'US'. Both upper and lower case allowed.
   */
  reporterCountryCode: string;
  /**
   *  The Spotify URI of the reported content.
   */
  productUri: string;
  /**
   *  This is the asset of the reported Entity (URI)
   */
  contentType: ContentType;
  /**
   *  The reason for reporting the content.
   */
  reason: Reason;
  /**
   *  The user agent from the reporter of the report
   */
  reporterUserAgent: string;
  experiments: Experiment[];
  queryParams: QueryParams | undefined;
  skipMetadataEnrichment: boolean;
  entityType: EntityType;
  assetId?: string | undefined;
  /**
   *  Collected based on the username provided
   */
  userId?: string | undefined;
  username?: string | undefined;
  associatedEntity?: AssociatedEntity | undefined;
}

export interface EmployeeContentPolicyReport {
  /**
   *  The email of the reporter. Needs to be the same email as validated on the first step.
   */
  reporterEmail: string;
  /**
   *  The two character country code of the reporter. E.g. 'SE' or 'US'. Both upper and lower case allowed.
   */
  reporterCountryCode: string;
  /**
   *  The Spotify URI of the reported content.
   */
  productUri: string;
  /**
   *  The type of the reported content.
   */
  contentType: ContentType;
  /**
   *  The team of the reporter.
   */
  team: string;
  /**
   *  The urgency of the report.
   */
  urgency: Urgency;
  /**
   *  Free-form text with more context about the report.
   */
  additionalInfo: string;
  /**
   *  The reason for reporting the content.
   */
  reason: Reason;
  /**
   *  The user agent from the reporter of the report
   */
  reporterUserAgent: string;
  experiments: Experiment[];
  queryParams: QueryParams | undefined;
  entityType: EntityType;
  assetId?: string | undefined;
  /**
   *  Collected based on the username provided
   */
  userId?: string | undefined;
  username?: string | undefined;
  associatedEntity?: AssociatedEntity | undefined;
}

/**
 *  Used for associated content, i.e. selecting an image from the Image Picker
 */
export interface AssociatedEntity {
  entityId: string;
  assetId: string;
}

const baseGetUserDataRequest: object = {
};

const baseGetUserDataTypeResponse: object = {
};

const baseGetEmailTypeRequest: object = {
  username: "",
};

const baseGetEmailTypeResponse: object = {
  emailType: 0,
};

const baseGetCodeRequest: object = {
  email: "",
};

const baseGetCodeResponse: object = {
  emailType: 0,
  resourceUri: "",
};

const baseValidateCodeRequest: object = {
  email: "",
  resourceUri: "",
  code: "",
};

const baseExternalContentPolicyReportRequest: object = {
};

const baseEmployeeContentPolicyReportRequest: object = {
};

const baseExperiment: object = {
  name: "",
  group: "",
};

const baseVerificationDetails: object = {
  googleRecaptchaToken: "",
};

const baseExternalContentPolicyReport: object = {
  reporterEmail: "",
  reporterCountryCode: "",
  productUri: "",
  contentType: 0,
  reason: 0,
  reporterUserAgent: "",
  skipMetadataEnrichment: false,
  entityType: 0,
};

const baseEmployeeContentPolicyReport: object = {
  reporterEmail: "",
  reporterCountryCode: "",
  productUri: "",
  contentType: 0,
  team: "",
  urgency: 0,
  additionalInfo: "",
  reason: 0,
  reporterUserAgent: "",
  entityType: 0,
};

const baseAssociatedEntity: object = {
  entityId: "",
  assetId: "",
};

export const protobufPackage = 'spotify.contentpolicy.v2'

export enum ReportOriginType {
  REPORT_ORIGIN_INVALID = 0,
  REPORT_ORIGIN_POLICY = 1,
  REPORT_ORIGIN_INFRINGEMENT = 2,
}

export function reportOriginTypeFromJSON(object: any): ReportOriginType {
  switch (object) {
    case 0:
    case "REPORT_ORIGIN_INVALID":
      return ReportOriginType.REPORT_ORIGIN_INVALID;
    case 1:
    case "REPORT_ORIGIN_POLICY":
      return ReportOriginType.REPORT_ORIGIN_POLICY;
    case 2:
    case "REPORT_ORIGIN_INFRINGEMENT":
      return ReportOriginType.REPORT_ORIGIN_INFRINGEMENT;
    default:
      throw new globalThis.Error("Unrecognized enum value " + object + " for enum ReportOriginType");
  }
}

export function reportOriginTypeToJSON(object: ReportOriginType): string {
  switch (object) {
    case ReportOriginType.REPORT_ORIGIN_INVALID:
      return "REPORT_ORIGIN_INVALID";
    case ReportOriginType.REPORT_ORIGIN_POLICY:
      return "REPORT_ORIGIN_POLICY";
    case ReportOriginType.REPORT_ORIGIN_INFRINGEMENT:
      return "REPORT_ORIGIN_INFRINGEMENT";
    default:
      return "UNKNOWN";
  }
}

export enum EmailType {
  EMAIL_TYPE_INVALID = 0,
  EMAIL_TYPE_SPOTIFY = 1,
  EMAIL_TYPE_EXTERNAL = 2,
}

export function emailTypeFromJSON(object: any): EmailType {
  switch (object) {
    case 0:
    case "EMAIL_TYPE_INVALID":
      return EmailType.EMAIL_TYPE_INVALID;
    case 1:
    case "EMAIL_TYPE_SPOTIFY":
      return EmailType.EMAIL_TYPE_SPOTIFY;
    case 2:
    case "EMAIL_TYPE_EXTERNAL":
      return EmailType.EMAIL_TYPE_EXTERNAL;
    default:
      throw new globalThis.Error("Unrecognized enum value " + object + " for enum EmailType");
  }
}

export function emailTypeToJSON(object: EmailType): string {
  switch (object) {
    case EmailType.EMAIL_TYPE_INVALID:
      return "EMAIL_TYPE_INVALID";
    case EmailType.EMAIL_TYPE_SPOTIFY:
      return "EMAIL_TYPE_SPOTIFY";
    case EmailType.EMAIL_TYPE_EXTERNAL:
      return "EMAIL_TYPE_EXTERNAL";
    default:
      return "UNKNOWN";
  }
}

/**  Currently for content types we tend to be very specific given our current i18n approach.
 We are planning an architectural change for the second version of the interface,
 therefore ensure you checked the team's ADR (Architectural Decision Record) for updates on this.
 */
export enum ContentType {
  CONTENT_TYPE_INVALID = 0,
  CONTENT_TYPE_MUSIC = 1,
  CONTENT_TYPE_PODCAST = 2,
  CONTENT_TYPE_ARTWORK = 3,
  CONTENT_TYPE_USER_IMAGE = 4,
  CONTENT_TYPE_PLAYLIST_IMAGE = 5,
  CONTENT_TYPE_PLAYLIST_TITLE = 6,
  CONTENT_TYPE_PLAYLIST_DESCRIPTION = 7,
  CONTENT_TYPE_ARTIST_PROFILE_IMAGE = 8,
  CONTENT_TYPE_ARTIST_BANNER_IMAGE = 9,
  CONTENT_TYPE_ARTIST_GALLERY_IMAGE = 10,
  CONTENT_TYPE_ARTIST_BIO_TEXT = 11,
  CONTENT_TYPE_ARTIST_PICK_TEXT = 12,
  CONTENT_TYPE_NOW_PLAYING = 13,
  CONTENT_TYPE_MUSIC_STORYLINES = 14,
  CONTENT_TYPE_PODCAST_DESCRIPTION = 15,
  CONTENT_TYPE_AUDIO = 16,
  CONTENT_TYPE_ARTIST_MERCHANDISE = 17,
  CONTENT_TYPE_TITLE = 18,
  CONTENT_TYPE_AUDIOBOOK_NAME = 19,
  CONTENT_TYPE_AUDIOBOOK_DESCRIPTION = 20,
  CONTENT_TYPE_AUDIOBOOK_ARTWORK = 21,
  CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO = 22,
  CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION = 23,
  CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME = 24,
  CONTENT_TYPE_ARTIST_VIDEO_TITLE = 33,
  CONTENT_TYPE_ARTIST_VIDEO_VIDEO = 34,
  CONTENT_TYPE_ARTIST_VIDEO_AUDIO = 35,
  CONTENT_TYPE_POLL_QUESTION_TEXT = 36,
  CONTENT_TYPE_POLL_OPTIONS_TEXT = 37,
  CONTENT_TYPE_QUESTION_TEXT = 38,
  CONTENT_TYPE_RESPONSE_TEXT = 39,
  CONTENT_TYPE_MERCH_TITLE = 40,
  CONTENT_TYPE_MERCH_DESCRIPTION = 41,
  CONTENT_TYPE_MERCH_IMAGE_GALLERY = 42,
  CONTENT_TYPE_COMMENT_TEXT = 43,
  CONTENT_TYPE_MUSIC_VIDEO = 44,
  CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_VIDEO = 45,
  CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_AUDIO = 46,
  CONTENT_TYPE_PODCAST_THUMBNAIL_IMAGE = 47,
  CONTENT_TYPE_CHAT_MESSAGE_TEXT = 48,
  CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_TITLE = 49,
  CONTENT_TYPE_SUPPLEMENTARY_PDF = 50,
}

export function contentTypeFromJSON(object: any): ContentType {
  switch (object) {
    case 0:
    case "CONTENT_TYPE_INVALID":
      return ContentType.CONTENT_TYPE_INVALID;
    case 1:
    case "CONTENT_TYPE_MUSIC":
      return ContentType.CONTENT_TYPE_MUSIC;
    case 2:
    case "CONTENT_TYPE_PODCAST":
      return ContentType.CONTENT_TYPE_PODCAST;
    case 3:
    case "CONTENT_TYPE_ARTWORK":
      return ContentType.CONTENT_TYPE_ARTWORK;
    case 4:
    case "CONTENT_TYPE_USER_IMAGE":
      return ContentType.CONTENT_TYPE_USER_IMAGE;
    case 5:
    case "CONTENT_TYPE_PLAYLIST_IMAGE":
      return ContentType.CONTENT_TYPE_PLAYLIST_IMAGE;
    case 6:
    case "CONTENT_TYPE_PLAYLIST_TITLE":
      return ContentType.CONTENT_TYPE_PLAYLIST_TITLE;
    case 7:
    case "CONTENT_TYPE_PLAYLIST_DESCRIPTION":
      return ContentType.CONTENT_TYPE_PLAYLIST_DESCRIPTION;
    case 8:
    case "CONTENT_TYPE_ARTIST_PROFILE_IMAGE":
      return ContentType.CONTENT_TYPE_ARTIST_PROFILE_IMAGE;
    case 9:
    case "CONTENT_TYPE_ARTIST_BANNER_IMAGE":
      return ContentType.CONTENT_TYPE_ARTIST_BANNER_IMAGE;
    case 10:
    case "CONTENT_TYPE_ARTIST_GALLERY_IMAGE":
      return ContentType.CONTENT_TYPE_ARTIST_GALLERY_IMAGE;
    case 11:
    case "CONTENT_TYPE_ARTIST_BIO_TEXT":
      return ContentType.CONTENT_TYPE_ARTIST_BIO_TEXT;
    case 12:
    case "CONTENT_TYPE_ARTIST_PICK_TEXT":
      return ContentType.CONTENT_TYPE_ARTIST_PICK_TEXT;
    case 13:
    case "CONTENT_TYPE_NOW_PLAYING":
      return ContentType.CONTENT_TYPE_NOW_PLAYING;
    case 14:
    case "CONTENT_TYPE_MUSIC_STORYLINES":
      return ContentType.CONTENT_TYPE_MUSIC_STORYLINES;
    case 15:
    case "CONTENT_TYPE_PODCAST_DESCRIPTION":
      return ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION;
    case 16:
    case "CONTENT_TYPE_AUDIO":
      return ContentType.CONTENT_TYPE_AUDIO;
    case 17:
    case "CONTENT_TYPE_ARTIST_MERCHANDISE":
      return ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE;
    case 18:
    case "CONTENT_TYPE_TITLE":
      return ContentType.CONTENT_TYPE_TITLE;
    case 19:
    case "CONTENT_TYPE_AUDIOBOOK_NAME":
      return ContentType.CONTENT_TYPE_AUDIOBOOK_NAME;
    case 20:
    case "CONTENT_TYPE_AUDIOBOOK_DESCRIPTION":
      return ContentType.CONTENT_TYPE_AUDIOBOOK_DESCRIPTION;
    case 21:
    case "CONTENT_TYPE_AUDIOBOOK_ARTWORK":
      return ContentType.CONTENT_TYPE_AUDIOBOOK_ARTWORK;
    case 22:
    case "CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO":
      return ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO;
    case 23:
    case "CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION":
      return ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION;
    case 24:
    case "CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME":
      return ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME;
    case 33:
    case "CONTENT_TYPE_ARTIST_VIDEO_TITLE":
      return ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE;
    case 34:
    case "CONTENT_TYPE_ARTIST_VIDEO_VIDEO":
      return ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO;
    case 35:
    case "CONTENT_TYPE_ARTIST_VIDEO_AUDIO":
      return ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO;
    case 36:
    case "CONTENT_TYPE_POLL_QUESTION_TEXT":
      return ContentType.CONTENT_TYPE_POLL_QUESTION_TEXT;
    case 37:
    case "CONTENT_TYPE_POLL_OPTIONS_TEXT":
      return ContentType.CONTENT_TYPE_POLL_OPTIONS_TEXT;
    case 38:
    case "CONTENT_TYPE_QUESTION_TEXT":
      return ContentType.CONTENT_TYPE_QUESTION_TEXT;
    case 39:
    case "CONTENT_TYPE_RESPONSE_TEXT":
      return ContentType.CONTENT_TYPE_RESPONSE_TEXT;
    case 40:
    case "CONTENT_TYPE_MERCH_TITLE":
      return ContentType.CONTENT_TYPE_MERCH_TITLE;
    case 41:
    case "CONTENT_TYPE_MERCH_DESCRIPTION":
      return ContentType.CONTENT_TYPE_MERCH_DESCRIPTION;
    case 42:
    case "CONTENT_TYPE_MERCH_IMAGE_GALLERY":
      return ContentType.CONTENT_TYPE_MERCH_IMAGE_GALLERY;
    case 43:
    case "CONTENT_TYPE_COMMENT_TEXT":
      return ContentType.CONTENT_TYPE_COMMENT_TEXT;
    case 44:
    case "CONTENT_TYPE_MUSIC_VIDEO":
      return ContentType.CONTENT_TYPE_MUSIC_VIDEO;
    case 45:
    case "CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_VIDEO":
      return ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_VIDEO;
    case 46:
    case "CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_AUDIO":
      return ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_AUDIO;
    case 47:
    case "CONTENT_TYPE_PODCAST_THUMBNAIL_IMAGE":
      return ContentType.CONTENT_TYPE_PODCAST_THUMBNAIL_IMAGE;
    case 48:
    case "CONTENT_TYPE_CHAT_MESSAGE_TEXT":
      return ContentType.CONTENT_TYPE_CHAT_MESSAGE_TEXT;
    case 49:
    case "CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_TITLE":
      return ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_TITLE;
    case 50:
    case "CONTENT_TYPE_SUPPLEMENTARY_PDF":
      return ContentType.CONTENT_TYPE_SUPPLEMENTARY_PDF;
    default:
      throw new globalThis.Error("Unrecognized enum value " + object + " for enum ContentType");
  }
}

export function contentTypeToJSON(object: ContentType): string {
  switch (object) {
    case ContentType.CONTENT_TYPE_INVALID:
      return "CONTENT_TYPE_INVALID";
    case ContentType.CONTENT_TYPE_MUSIC:
      return "CONTENT_TYPE_MUSIC";
    case ContentType.CONTENT_TYPE_PODCAST:
      return "CONTENT_TYPE_PODCAST";
    case ContentType.CONTENT_TYPE_ARTWORK:
      return "CONTENT_TYPE_ARTWORK";
    case ContentType.CONTENT_TYPE_USER_IMAGE:
      return "CONTENT_TYPE_USER_IMAGE";
    case ContentType.CONTENT_TYPE_PLAYLIST_IMAGE:
      return "CONTENT_TYPE_PLAYLIST_IMAGE";
    case ContentType.CONTENT_TYPE_PLAYLIST_TITLE:
      return "CONTENT_TYPE_PLAYLIST_TITLE";
    case ContentType.CONTENT_TYPE_PLAYLIST_DESCRIPTION:
      return "CONTENT_TYPE_PLAYLIST_DESCRIPTION";
    case ContentType.CONTENT_TYPE_ARTIST_PROFILE_IMAGE:
      return "CONTENT_TYPE_ARTIST_PROFILE_IMAGE";
    case ContentType.CONTENT_TYPE_ARTIST_BANNER_IMAGE:
      return "CONTENT_TYPE_ARTIST_BANNER_IMAGE";
    case ContentType.CONTENT_TYPE_ARTIST_GALLERY_IMAGE:
      return "CONTENT_TYPE_ARTIST_GALLERY_IMAGE";
    case ContentType.CONTENT_TYPE_ARTIST_BIO_TEXT:
      return "CONTENT_TYPE_ARTIST_BIO_TEXT";
    case ContentType.CONTENT_TYPE_ARTIST_PICK_TEXT:
      return "CONTENT_TYPE_ARTIST_PICK_TEXT";
    case ContentType.CONTENT_TYPE_NOW_PLAYING:
      return "CONTENT_TYPE_NOW_PLAYING";
    case ContentType.CONTENT_TYPE_MUSIC_STORYLINES:
      return "CONTENT_TYPE_MUSIC_STORYLINES";
    case ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION:
      return "CONTENT_TYPE_PODCAST_DESCRIPTION";
    case ContentType.CONTENT_TYPE_AUDIO:
      return "CONTENT_TYPE_AUDIO";
    case ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE:
      return "CONTENT_TYPE_ARTIST_MERCHANDISE";
    case ContentType.CONTENT_TYPE_TITLE:
      return "CONTENT_TYPE_TITLE";
    case ContentType.CONTENT_TYPE_AUDIOBOOK_NAME:
      return "CONTENT_TYPE_AUDIOBOOK_NAME";
    case ContentType.CONTENT_TYPE_AUDIOBOOK_DESCRIPTION:
      return "CONTENT_TYPE_AUDIOBOOK_DESCRIPTION";
    case ContentType.CONTENT_TYPE_AUDIOBOOK_ARTWORK:
      return "CONTENT_TYPE_AUDIOBOOK_ARTWORK";
    case ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO:
      return "CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO";
    case ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION:
      return "CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION";
    case ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME:
      return "CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME";
    case ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE:
      return "CONTENT_TYPE_ARTIST_VIDEO_TITLE";
    case ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO:
      return "CONTENT_TYPE_ARTIST_VIDEO_VIDEO";
    case ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO:
      return "CONTENT_TYPE_ARTIST_VIDEO_AUDIO";
    case ContentType.CONTENT_TYPE_POLL_QUESTION_TEXT:
      return "CONTENT_TYPE_POLL_QUESTION_TEXT";
    case ContentType.CONTENT_TYPE_POLL_OPTIONS_TEXT:
      return "CONTENT_TYPE_POLL_OPTIONS_TEXT";
    case ContentType.CONTENT_TYPE_QUESTION_TEXT:
      return "CONTENT_TYPE_QUESTION_TEXT";
    case ContentType.CONTENT_TYPE_RESPONSE_TEXT:
      return "CONTENT_TYPE_RESPONSE_TEXT";
    case ContentType.CONTENT_TYPE_MERCH_TITLE:
      return "CONTENT_TYPE_MERCH_TITLE";
    case ContentType.CONTENT_TYPE_MERCH_DESCRIPTION:
      return "CONTENT_TYPE_MERCH_DESCRIPTION";
    case ContentType.CONTENT_TYPE_MERCH_IMAGE_GALLERY:
      return "CONTENT_TYPE_MERCH_IMAGE_GALLERY";
    case ContentType.CONTENT_TYPE_COMMENT_TEXT:
      return "CONTENT_TYPE_COMMENT_TEXT";
    case ContentType.CONTENT_TYPE_MUSIC_VIDEO:
      return "CONTENT_TYPE_MUSIC_VIDEO";
    case ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_VIDEO:
      return "CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_VIDEO";
    case ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_AUDIO:
      return "CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_AUDIO";
    case ContentType.CONTENT_TYPE_PODCAST_THUMBNAIL_IMAGE:
      return "CONTENT_TYPE_PODCAST_THUMBNAIL_IMAGE";
    case ContentType.CONTENT_TYPE_CHAT_MESSAGE_TEXT:
      return "CONTENT_TYPE_CHAT_MESSAGE_TEXT";
    case ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_TITLE:
      return "CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_TITLE";
    case ContentType.CONTENT_TYPE_SUPPLEMENTARY_PDF:
      return "CONTENT_TYPE_SUPPLEMENTARY_PDF";
    default:
      return "UNKNOWN";
  }
}

export enum Urgency {
  URGENCY_INVALID = 0,
  URGENCY_HIGH = 1,
  URGENCY_MEDIUM = 2,
  URGENCY_LOW = 3,
}

export function urgencyFromJSON(object: any): Urgency {
  switch (object) {
    case 0:
    case "URGENCY_INVALID":
      return Urgency.URGENCY_INVALID;
    case 1:
    case "URGENCY_HIGH":
      return Urgency.URGENCY_HIGH;
    case 2:
    case "URGENCY_MEDIUM":
      return Urgency.URGENCY_MEDIUM;
    case 3:
    case "URGENCY_LOW":
      return Urgency.URGENCY_LOW;
    default:
      throw new globalThis.Error("Unrecognized enum value " + object + " for enum Urgency");
  }
}

export function urgencyToJSON(object: Urgency): string {
  switch (object) {
    case Urgency.URGENCY_INVALID:
      return "URGENCY_INVALID";
    case Urgency.URGENCY_HIGH:
      return "URGENCY_HIGH";
    case Urgency.URGENCY_MEDIUM:
      return "URGENCY_MEDIUM";
    case Urgency.URGENCY_LOW:
      return "URGENCY_LOW";
    default:
      return "UNKNOWN";
  }
}

export enum Reason {
  REASON_INVALID = 0,
  REASON_SEXUAL = 1,
  REASON_VIOLENT_OR_DANGEROUS = 2,
  REASON_HATEFUL_OR_ABUSIVE = 3,
  REASON_DECEPTIVE = 4,
  REASON_DO_NOT_LIKE_IT = 5,
  REASON_CSEA = 6,
}

export function reasonFromJSON(object: any): Reason {
  switch (object) {
    case 0:
    case "REASON_INVALID":
      return Reason.REASON_INVALID;
    case 1:
    case "REASON_SEXUAL":
      return Reason.REASON_SEXUAL;
    case 2:
    case "REASON_VIOLENT_OR_DANGEROUS":
      return Reason.REASON_VIOLENT_OR_DANGEROUS;
    case 3:
    case "REASON_HATEFUL_OR_ABUSIVE":
      return Reason.REASON_HATEFUL_OR_ABUSIVE;
    case 4:
    case "REASON_DECEPTIVE":
      return Reason.REASON_DECEPTIVE;
    case 5:
    case "REASON_DO_NOT_LIKE_IT":
      return Reason.REASON_DO_NOT_LIKE_IT;
    case 6:
    case "REASON_CSEA":
      return Reason.REASON_CSEA;
    default:
      throw new globalThis.Error("Unrecognized enum value " + object + " for enum Reason");
  }
}

export function reasonToJSON(object: Reason): string {
  switch (object) {
    case Reason.REASON_INVALID:
      return "REASON_INVALID";
    case Reason.REASON_SEXUAL:
      return "REASON_SEXUAL";
    case Reason.REASON_VIOLENT_OR_DANGEROUS:
      return "REASON_VIOLENT_OR_DANGEROUS";
    case Reason.REASON_HATEFUL_OR_ABUSIVE:
      return "REASON_HATEFUL_OR_ABUSIVE";
    case Reason.REASON_DECEPTIVE:
      return "REASON_DECEPTIVE";
    case Reason.REASON_DO_NOT_LIKE_IT:
      return "REASON_DO_NOT_LIKE_IT";
    case Reason.REASON_CSEA:
      return "REASON_CSEA";
    default:
      return "UNKNOWN";
  }
}

export const GetUserDataRequest = {
  fromJSON(object: any): GetUserDataRequest {
    const message = { ...baseGetUserDataRequest } as GetUserDataRequest;
    if (object.username !== undefined && object.username !== null) {
      message.username = String(object.username);
    } else {
      message.username = undefined;
    }
    if (object.email !== undefined && object.email !== null) {
      message.email = String(object.email);
    } else {
      message.email = undefined;
    }
    if (object.reportOrigin !== undefined && object.reportOrigin !== null) {
      message.reportOrigin = reportOriginTypeFromJSON(object.reportOrigin);
    } else {
      message.reportOrigin = undefined;
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetUserDataRequest>): GetUserDataRequest {
    const message = { ...baseGetUserDataRequest } as GetUserDataRequest;
    if (object.username !== undefined && object.username !== null) {
      message.username = object.username;
    } else {
      message.username = undefined;
    }
    if (object.email !== undefined && object.email !== null) {
      message.email = object.email;
    } else {
      message.email = undefined;
    }
    if (object.reportOrigin !== undefined && object.reportOrigin !== null) {
      message.reportOrigin = object.reportOrigin;
    } else {
      message.reportOrigin = undefined;
    }
    return message;
  },
  toJSON(message: GetUserDataRequest): unknown {
    const obj: any = {};
    message.username !== undefined && (obj.username = message.username);
    message.email !== undefined && (obj.email = message.email);
    message.reportOrigin !== undefined && (obj.reportOrigin = message.reportOrigin !== undefined ? reportOriginTypeToJSON(message.reportOrigin) : undefined);
    return obj;
  },
};

export const GetUserDataTypeResponse = {
  fromJSON(object: any): GetUserDataTypeResponse {
    const message = { ...baseGetUserDataTypeResponse } as GetUserDataTypeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = emailTypeFromJSON(object.emailType);
    } else {
      message.emailType = undefined;
    }
    if (object.country !== undefined && object.country !== null) {
      message.country = String(object.country);
    } else {
      message.country = undefined;
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetUserDataTypeResponse>): GetUserDataTypeResponse {
    const message = { ...baseGetUserDataTypeResponse } as GetUserDataTypeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = object.emailType;
    } else {
      message.emailType = undefined;
    }
    if (object.country !== undefined && object.country !== null) {
      message.country = object.country;
    } else {
      message.country = undefined;
    }
    return message;
  },
  toJSON(message: GetUserDataTypeResponse): unknown {
    const obj: any = {};
    message.emailType !== undefined && (obj.emailType = message.emailType !== undefined ? emailTypeToJSON(message.emailType) : undefined);
    message.country !== undefined && (obj.country = message.country);
    return obj;
  },
};

export const GetEmailTypeRequest = {
  fromJSON(object: any): GetEmailTypeRequest {
    const message = { ...baseGetEmailTypeRequest } as GetEmailTypeRequest;
    if (object.username !== undefined && object.username !== null) {
      message.username = String(object.username);
    } else {
      message.username = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetEmailTypeRequest>): GetEmailTypeRequest {
    const message = { ...baseGetEmailTypeRequest } as GetEmailTypeRequest;
    if (object.username !== undefined && object.username !== null) {
      message.username = object.username;
    } else {
      message.username = "";
    }
    return message;
  },
  toJSON(message: GetEmailTypeRequest): unknown {
    const obj: any = {};
    message.username !== undefined && (obj.username = message.username);
    return obj;
  },
};

export const GetEmailTypeResponse = {
  fromJSON(object: any): GetEmailTypeResponse {
    const message = { ...baseGetEmailTypeResponse } as GetEmailTypeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = emailTypeFromJSON(object.emailType);
    } else {
      message.emailType = 0;
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetEmailTypeResponse>): GetEmailTypeResponse {
    const message = { ...baseGetEmailTypeResponse } as GetEmailTypeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = object.emailType;
    } else {
      message.emailType = 0;
    }
    return message;
  },
  toJSON(message: GetEmailTypeResponse): unknown {
    const obj: any = {};
    message.emailType !== undefined && (obj.emailType = emailTypeToJSON(message.emailType));
    return obj;
  },
};

export const GetCodeRequest = {
  fromJSON(object: any): GetCodeRequest {
    const message = { ...baseGetCodeRequest } as GetCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = String(object.email);
    } else {
      message.email = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetCodeRequest>): GetCodeRequest {
    const message = { ...baseGetCodeRequest } as GetCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = object.email;
    } else {
      message.email = "";
    }
    return message;
  },
  toJSON(message: GetCodeRequest): unknown {
    const obj: any = {};
    message.email !== undefined && (obj.email = message.email);
    return obj;
  },
};

export const GetCodeResponse = {
  fromJSON(object: any): GetCodeResponse {
    const message = { ...baseGetCodeResponse } as GetCodeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = emailTypeFromJSON(object.emailType);
    } else {
      message.emailType = 0;
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = String(object.resourceUri);
    } else {
      message.resourceUri = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetCodeResponse>): GetCodeResponse {
    const message = { ...baseGetCodeResponse } as GetCodeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = object.emailType;
    } else {
      message.emailType = 0;
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = object.resourceUri;
    } else {
      message.resourceUri = "";
    }
    return message;
  },
  toJSON(message: GetCodeResponse): unknown {
    const obj: any = {};
    message.emailType !== undefined && (obj.emailType = emailTypeToJSON(message.emailType));
    message.resourceUri !== undefined && (obj.resourceUri = message.resourceUri);
    return obj;
  },
};

export const ValidateCodeRequest = {
  fromJSON(object: any): ValidateCodeRequest {
    const message = { ...baseValidateCodeRequest } as ValidateCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = String(object.email);
    } else {
      message.email = "";
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = String(object.resourceUri);
    } else {
      message.resourceUri = "";
    }
    if (object.code !== undefined && object.code !== null) {
      message.code = String(object.code);
    } else {
      message.code = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<ValidateCodeRequest>): ValidateCodeRequest {
    const message = { ...baseValidateCodeRequest } as ValidateCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = object.email;
    } else {
      message.email = "";
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = object.resourceUri;
    } else {
      message.resourceUri = "";
    }
    if (object.code !== undefined && object.code !== null) {
      message.code = object.code;
    } else {
      message.code = "";
    }
    return message;
  },
  toJSON(message: ValidateCodeRequest): unknown {
    const obj: any = {};
    message.email !== undefined && (obj.email = message.email);
    message.resourceUri !== undefined && (obj.resourceUri = message.resourceUri);
    message.code !== undefined && (obj.code = message.code);
    return obj;
  },
};

export const ExternalContentPolicyReportRequest = {
  fromJSON(object: any): ExternalContentPolicyReportRequest {
    const message = { ...baseExternalContentPolicyReportRequest } as ExternalContentPolicyReportRequest;
    if (object.verificationDetails !== undefined && object.verificationDetails !== null) {
      message.verificationDetails = VerificationDetails.fromJSON(object.verificationDetails);
    } else {
      message.verificationDetails = undefined;
    }
    if (object.report !== undefined && object.report !== null) {
      message.report = ExternalContentPolicyReport.fromJSON(object.report);
    } else {
      message.report = undefined;
    }
    return message;
  },
  fromPartial(object: DeepPartial<ExternalContentPolicyReportRequest>): ExternalContentPolicyReportRequest {
    const message = { ...baseExternalContentPolicyReportRequest } as ExternalContentPolicyReportRequest;
    if (object.verificationDetails !== undefined && object.verificationDetails !== null) {
      message.verificationDetails = VerificationDetails.fromPartial(object.verificationDetails);
    } else {
      message.verificationDetails = undefined;
    }
    if (object.report !== undefined && object.report !== null) {
      message.report = ExternalContentPolicyReport.fromPartial(object.report);
    } else {
      message.report = undefined;
    }
    return message;
  },
  toJSON(message: ExternalContentPolicyReportRequest): unknown {
    const obj: any = {};
    message.verificationDetails !== undefined && (obj.verificationDetails = message.verificationDetails ? VerificationDetails.toJSON(message.verificationDetails) : undefined);
    message.report !== undefined && (obj.report = message.report ? ExternalContentPolicyReport.toJSON(message.report) : undefined);
    return obj;
  },
};

export const EmployeeContentPolicyReportRequest = {
  fromJSON(object: any): EmployeeContentPolicyReportRequest {
    const message = { ...baseEmployeeContentPolicyReportRequest } as EmployeeContentPolicyReportRequest;
    if (object.verificationDetails !== undefined && object.verificationDetails !== null) {
      message.verificationDetails = VerificationDetails.fromJSON(object.verificationDetails);
    } else {
      message.verificationDetails = undefined;
    }
    if (object.report !== undefined && object.report !== null) {
      message.report = EmployeeContentPolicyReport.fromJSON(object.report);
    } else {
      message.report = undefined;
    }
    return message;
  },
  fromPartial(object: DeepPartial<EmployeeContentPolicyReportRequest>): EmployeeContentPolicyReportRequest {
    const message = { ...baseEmployeeContentPolicyReportRequest } as EmployeeContentPolicyReportRequest;
    if (object.verificationDetails !== undefined && object.verificationDetails !== null) {
      message.verificationDetails = VerificationDetails.fromPartial(object.verificationDetails);
    } else {
      message.verificationDetails = undefined;
    }
    if (object.report !== undefined && object.report !== null) {
      message.report = EmployeeContentPolicyReport.fromPartial(object.report);
    } else {
      message.report = undefined;
    }
    return message;
  },
  toJSON(message: EmployeeContentPolicyReportRequest): unknown {
    const obj: any = {};
    message.verificationDetails !== undefined && (obj.verificationDetails = message.verificationDetails ? VerificationDetails.toJSON(message.verificationDetails) : undefined);
    message.report !== undefined && (obj.report = message.report ? EmployeeContentPolicyReport.toJSON(message.report) : undefined);
    return obj;
  },
};

export const Experiment = {
  fromJSON(object: any): Experiment {
    const message = { ...baseExperiment } as Experiment;
    if (object.name !== undefined && object.name !== null) {
      message.name = String(object.name);
    } else {
      message.name = "";
    }
    if (object.group !== undefined && object.group !== null) {
      message.group = String(object.group);
    } else {
      message.group = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<Experiment>): Experiment {
    const message = { ...baseExperiment } as Experiment;
    if (object.name !== undefined && object.name !== null) {
      message.name = object.name;
    } else {
      message.name = "";
    }
    if (object.group !== undefined && object.group !== null) {
      message.group = object.group;
    } else {
      message.group = "";
    }
    return message;
  },
  toJSON(message: Experiment): unknown {
    const obj: any = {};
    message.name !== undefined && (obj.name = message.name);
    message.group !== undefined && (obj.group = message.group);
    return obj;
  },
};

export const VerificationDetails = {
  fromJSON(object: any): VerificationDetails {
    const message = { ...baseVerificationDetails } as VerificationDetails;
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = String(object.resourceUri);
    } else {
      message.resourceUri = undefined;
    }
    if (object.code !== undefined && object.code !== null) {
      message.code = String(object.code);
    } else {
      message.code = undefined;
    }
    if (object.googleRecaptchaToken !== undefined && object.googleRecaptchaToken !== null) {
      message.googleRecaptchaToken = String(object.googleRecaptchaToken);
    } else {
      message.googleRecaptchaToken = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<VerificationDetails>): VerificationDetails {
    const message = { ...baseVerificationDetails } as VerificationDetails;
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = object.resourceUri;
    } else {
      message.resourceUri = undefined;
    }
    if (object.code !== undefined && object.code !== null) {
      message.code = object.code;
    } else {
      message.code = undefined;
    }
    if (object.googleRecaptchaToken !== undefined && object.googleRecaptchaToken !== null) {
      message.googleRecaptchaToken = object.googleRecaptchaToken;
    } else {
      message.googleRecaptchaToken = "";
    }
    return message;
  },
  toJSON(message: VerificationDetails): unknown {
    const obj: any = {};
    message.resourceUri !== undefined && (obj.resourceUri = message.resourceUri);
    message.code !== undefined && (obj.code = message.code);
    message.googleRecaptchaToken !== undefined && (obj.googleRecaptchaToken = message.googleRecaptchaToken);
    return obj;
  },
};

export const ExternalContentPolicyReport = {
  fromJSON(object: any): ExternalContentPolicyReport {
    const message = { ...baseExternalContentPolicyReport } as ExternalContentPolicyReport;
    message.experiments = [];
    if (object.reporterEmail !== undefined && object.reporterEmail !== null) {
      message.reporterEmail = String(object.reporterEmail);
    } else {
      message.reporterEmail = "";
    }
    if (object.reporterCountryCode !== undefined && object.reporterCountryCode !== null) {
      message.reporterCountryCode = String(object.reporterCountryCode);
    } else {
      message.reporterCountryCode = "";
    }
    if (object.productUri !== undefined && object.productUri !== null) {
      message.productUri = String(object.productUri);
    } else {
      message.productUri = "";
    }
    if (object.contentType !== undefined && object.contentType !== null) {
      message.contentType = contentTypeFromJSON(object.contentType);
    } else {
      message.contentType = 0;
    }
    if (object.reason !== undefined && object.reason !== null) {
      message.reason = reasonFromJSON(object.reason);
    } else {
      message.reason = 0;
    }
    if (object.reporterUserAgent !== undefined && object.reporterUserAgent !== null) {
      message.reporterUserAgent = String(object.reporterUserAgent);
    } else {
      message.reporterUserAgent = "";
    }
    if (object.experiments !== undefined && object.experiments !== null) {
      for (const e of object.experiments) {
        message.experiments.push(Experiment.fromJSON(e));
      }
    }
    if (object.queryParams !== undefined && object.queryParams !== null) {
      message.queryParams = QueryParams.fromJSON(object.queryParams);
    } else {
      message.queryParams = undefined;
    }
    if (object.skipMetadataEnrichment !== undefined && object.skipMetadataEnrichment !== null) {
      message.skipMetadataEnrichment = Boolean(object.skipMetadataEnrichment);
    } else {
      message.skipMetadataEnrichment = false;
    }
    if (object.entityType !== undefined && object.entityType !== null) {
      message.entityType = entityTypeFromJSON(object.entityType);
    } else {
      message.entityType = 0;
    }
    if (object.assetId !== undefined && object.assetId !== null) {
      message.assetId = String(object.assetId);
    } else {
      message.assetId = undefined;
    }
    if (object.userId !== undefined && object.userId !== null) {
      message.userId = String(object.userId);
    } else {
      message.userId = undefined;
    }
    if (object.username !== undefined && object.username !== null) {
      message.username = String(object.username);
    } else {
      message.username = undefined;
    }
    if (object.associatedEntity !== undefined && object.associatedEntity !== null) {
      message.associatedEntity = AssociatedEntity.fromJSON(object.associatedEntity);
    } else {
      message.associatedEntity = undefined;
    }
    return message;
  },
  fromPartial(object: DeepPartial<ExternalContentPolicyReport>): ExternalContentPolicyReport {
    const message = { ...baseExternalContentPolicyReport } as ExternalContentPolicyReport;
    message.experiments = [];
    if (object.reporterEmail !== undefined && object.reporterEmail !== null) {
      message.reporterEmail = object.reporterEmail;
    } else {
      message.reporterEmail = "";
    }
    if (object.reporterCountryCode !== undefined && object.reporterCountryCode !== null) {
      message.reporterCountryCode = object.reporterCountryCode;
    } else {
      message.reporterCountryCode = "";
    }
    if (object.productUri !== undefined && object.productUri !== null) {
      message.productUri = object.productUri;
    } else {
      message.productUri = "";
    }
    if (object.contentType !== undefined && object.contentType !== null) {
      message.contentType = object.contentType;
    } else {
      message.contentType = 0;
    }
    if (object.reason !== undefined && object.reason !== null) {
      message.reason = object.reason;
    } else {
      message.reason = 0;
    }
    if (object.reporterUserAgent !== undefined && object.reporterUserAgent !== null) {
      message.reporterUserAgent = object.reporterUserAgent;
    } else {
      message.reporterUserAgent = "";
    }
    if (object.experiments !== undefined && object.experiments !== null) {
      for (const e of object.experiments) {
        message.experiments.push(Experiment.fromPartial(e));
      }
    }
    if (object.queryParams !== undefined && object.queryParams !== null) {
      message.queryParams = QueryParams.fromPartial(object.queryParams);
    } else {
      message.queryParams = undefined;
    }
    if (object.skipMetadataEnrichment !== undefined && object.skipMetadataEnrichment !== null) {
      message.skipMetadataEnrichment = object.skipMetadataEnrichment;
    } else {
      message.skipMetadataEnrichment = false;
    }
    if (object.entityType !== undefined && object.entityType !== null) {
      message.entityType = object.entityType;
    } else {
      message.entityType = 0;
    }
    if (object.assetId !== undefined && object.assetId !== null) {
      message.assetId = object.assetId;
    } else {
      message.assetId = undefined;
    }
    if (object.userId !== undefined && object.userId !== null) {
      message.userId = object.userId;
    } else {
      message.userId = undefined;
    }
    if (object.username !== undefined && object.username !== null) {
      message.username = object.username;
    } else {
      message.username = undefined;
    }
    if (object.associatedEntity !== undefined && object.associatedEntity !== null) {
      message.associatedEntity = AssociatedEntity.fromPartial(object.associatedEntity);
    } else {
      message.associatedEntity = undefined;
    }
    return message;
  },
  toJSON(message: ExternalContentPolicyReport): unknown {
    const obj: any = {};
    message.reporterEmail !== undefined && (obj.reporterEmail = message.reporterEmail);
    message.reporterCountryCode !== undefined && (obj.reporterCountryCode = message.reporterCountryCode);
    message.productUri !== undefined && (obj.productUri = message.productUri);
    message.contentType !== undefined && (obj.contentType = contentTypeToJSON(message.contentType));
    message.reason !== undefined && (obj.reason = reasonToJSON(message.reason));
    message.reporterUserAgent !== undefined && (obj.reporterUserAgent = message.reporterUserAgent);
    if (message.experiments) {
      obj.experiments = message.experiments.map(e => e ? Experiment.toJSON(e) : undefined);
    } else {
      obj.experiments = [];
    }
    message.queryParams !== undefined && (obj.queryParams = message.queryParams ? QueryParams.toJSON(message.queryParams) : undefined);
    message.skipMetadataEnrichment !== undefined && (obj.skipMetadataEnrichment = message.skipMetadataEnrichment);
    message.entityType !== undefined && (obj.entityType = entityTypeToJSON(message.entityType));
    message.assetId !== undefined && (obj.assetId = message.assetId);
    message.userId !== undefined && (obj.userId = message.userId);
    message.username !== undefined && (obj.username = message.username);
    message.associatedEntity !== undefined && (obj.associatedEntity = message.associatedEntity ? AssociatedEntity.toJSON(message.associatedEntity) : undefined);
    return obj;
  },
};

export const EmployeeContentPolicyReport = {
  fromJSON(object: any): EmployeeContentPolicyReport {
    const message = { ...baseEmployeeContentPolicyReport } as EmployeeContentPolicyReport;
    message.experiments = [];
    if (object.reporterEmail !== undefined && object.reporterEmail !== null) {
      message.reporterEmail = String(object.reporterEmail);
    } else {
      message.reporterEmail = "";
    }
    if (object.reporterCountryCode !== undefined && object.reporterCountryCode !== null) {
      message.reporterCountryCode = String(object.reporterCountryCode);
    } else {
      message.reporterCountryCode = "";
    }
    if (object.productUri !== undefined && object.productUri !== null) {
      message.productUri = String(object.productUri);
    } else {
      message.productUri = "";
    }
    if (object.contentType !== undefined && object.contentType !== null) {
      message.contentType = contentTypeFromJSON(object.contentType);
    } else {
      message.contentType = 0;
    }
    if (object.team !== undefined && object.team !== null) {
      message.team = String(object.team);
    } else {
      message.team = "";
    }
    if (object.urgency !== undefined && object.urgency !== null) {
      message.urgency = urgencyFromJSON(object.urgency);
    } else {
      message.urgency = 0;
    }
    if (object.additionalInfo !== undefined && object.additionalInfo !== null) {
      message.additionalInfo = String(object.additionalInfo);
    } else {
      message.additionalInfo = "";
    }
    if (object.reason !== undefined && object.reason !== null) {
      message.reason = reasonFromJSON(object.reason);
    } else {
      message.reason = 0;
    }
    if (object.reporterUserAgent !== undefined && object.reporterUserAgent !== null) {
      message.reporterUserAgent = String(object.reporterUserAgent);
    } else {
      message.reporterUserAgent = "";
    }
    if (object.experiments !== undefined && object.experiments !== null) {
      for (const e of object.experiments) {
        message.experiments.push(Experiment.fromJSON(e));
      }
    }
    if (object.queryParams !== undefined && object.queryParams !== null) {
      message.queryParams = QueryParams.fromJSON(object.queryParams);
    } else {
      message.queryParams = undefined;
    }
    if (object.entityType !== undefined && object.entityType !== null) {
      message.entityType = entityTypeFromJSON(object.entityType);
    } else {
      message.entityType = 0;
    }
    if (object.assetId !== undefined && object.assetId !== null) {
      message.assetId = String(object.assetId);
    } else {
      message.assetId = undefined;
    }
    if (object.userId !== undefined && object.userId !== null) {
      message.userId = String(object.userId);
    } else {
      message.userId = undefined;
    }
    if (object.username !== undefined && object.username !== null) {
      message.username = String(object.username);
    } else {
      message.username = undefined;
    }
    if (object.associatedEntity !== undefined && object.associatedEntity !== null) {
      message.associatedEntity = AssociatedEntity.fromJSON(object.associatedEntity);
    } else {
      message.associatedEntity = undefined;
    }
    return message;
  },
  fromPartial(object: DeepPartial<EmployeeContentPolicyReport>): EmployeeContentPolicyReport {
    const message = { ...baseEmployeeContentPolicyReport } as EmployeeContentPolicyReport;
    message.experiments = [];
    if (object.reporterEmail !== undefined && object.reporterEmail !== null) {
      message.reporterEmail = object.reporterEmail;
    } else {
      message.reporterEmail = "";
    }
    if (object.reporterCountryCode !== undefined && object.reporterCountryCode !== null) {
      message.reporterCountryCode = object.reporterCountryCode;
    } else {
      message.reporterCountryCode = "";
    }
    if (object.productUri !== undefined && object.productUri !== null) {
      message.productUri = object.productUri;
    } else {
      message.productUri = "";
    }
    if (object.contentType !== undefined && object.contentType !== null) {
      message.contentType = object.contentType;
    } else {
      message.contentType = 0;
    }
    if (object.team !== undefined && object.team !== null) {
      message.team = object.team;
    } else {
      message.team = "";
    }
    if (object.urgency !== undefined && object.urgency !== null) {
      message.urgency = object.urgency;
    } else {
      message.urgency = 0;
    }
    if (object.additionalInfo !== undefined && object.additionalInfo !== null) {
      message.additionalInfo = object.additionalInfo;
    } else {
      message.additionalInfo = "";
    }
    if (object.reason !== undefined && object.reason !== null) {
      message.reason = object.reason;
    } else {
      message.reason = 0;
    }
    if (object.reporterUserAgent !== undefined && object.reporterUserAgent !== null) {
      message.reporterUserAgent = object.reporterUserAgent;
    } else {
      message.reporterUserAgent = "";
    }
    if (object.experiments !== undefined && object.experiments !== null) {
      for (const e of object.experiments) {
        message.experiments.push(Experiment.fromPartial(e));
      }
    }
    if (object.queryParams !== undefined && object.queryParams !== null) {
      message.queryParams = QueryParams.fromPartial(object.queryParams);
    } else {
      message.queryParams = undefined;
    }
    if (object.entityType !== undefined && object.entityType !== null) {
      message.entityType = object.entityType;
    } else {
      message.entityType = 0;
    }
    if (object.assetId !== undefined && object.assetId !== null) {
      message.assetId = object.assetId;
    } else {
      message.assetId = undefined;
    }
    if (object.userId !== undefined && object.userId !== null) {
      message.userId = object.userId;
    } else {
      message.userId = undefined;
    }
    if (object.username !== undefined && object.username !== null) {
      message.username = object.username;
    } else {
      message.username = undefined;
    }
    if (object.associatedEntity !== undefined && object.associatedEntity !== null) {
      message.associatedEntity = AssociatedEntity.fromPartial(object.associatedEntity);
    } else {
      message.associatedEntity = undefined;
    }
    return message;
  },
  toJSON(message: EmployeeContentPolicyReport): unknown {
    const obj: any = {};
    message.reporterEmail !== undefined && (obj.reporterEmail = message.reporterEmail);
    message.reporterCountryCode !== undefined && (obj.reporterCountryCode = message.reporterCountryCode);
    message.productUri !== undefined && (obj.productUri = message.productUri);
    message.contentType !== undefined && (obj.contentType = contentTypeToJSON(message.contentType));
    message.team !== undefined && (obj.team = message.team);
    message.urgency !== undefined && (obj.urgency = urgencyToJSON(message.urgency));
    message.additionalInfo !== undefined && (obj.additionalInfo = message.additionalInfo);
    message.reason !== undefined && (obj.reason = reasonToJSON(message.reason));
    message.reporterUserAgent !== undefined && (obj.reporterUserAgent = message.reporterUserAgent);
    if (message.experiments) {
      obj.experiments = message.experiments.map(e => e ? Experiment.toJSON(e) : undefined);
    } else {
      obj.experiments = [];
    }
    message.queryParams !== undefined && (obj.queryParams = message.queryParams ? QueryParams.toJSON(message.queryParams) : undefined);
    message.entityType !== undefined && (obj.entityType = entityTypeToJSON(message.entityType));
    message.assetId !== undefined && (obj.assetId = message.assetId);
    message.userId !== undefined && (obj.userId = message.userId);
    message.username !== undefined && (obj.username = message.username);
    message.associatedEntity !== undefined && (obj.associatedEntity = message.associatedEntity ? AssociatedEntity.toJSON(message.associatedEntity) : undefined);
    return obj;
  },
};

export const AssociatedEntity = {
  fromJSON(object: any): AssociatedEntity {
    const message = { ...baseAssociatedEntity } as AssociatedEntity;
    if (object.entityId !== undefined && object.entityId !== null) {
      message.entityId = String(object.entityId);
    } else {
      message.entityId = "";
    }
    if (object.assetId !== undefined && object.assetId !== null) {
      message.assetId = String(object.assetId);
    } else {
      message.assetId = "";
    }
    return message;
  },
  fromPartial(object: DeepPartial<AssociatedEntity>): AssociatedEntity {
    const message = { ...baseAssociatedEntity } as AssociatedEntity;
    if (object.entityId !== undefined && object.entityId !== null) {
      message.entityId = object.entityId;
    } else {
      message.entityId = "";
    }
    if (object.assetId !== undefined && object.assetId !== null) {
      message.assetId = object.assetId;
    } else {
      message.assetId = "";
    }
    return message;
  },
  toJSON(message: AssociatedEntity): unknown {
    const obj: any = {};
    message.entityId !== undefined && (obj.entityId = message.entityId);
    message.assetId !== undefined && (obj.assetId = message.assetId);
    return obj;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | undefined;
type DeepPartial<T> = T extends Builtin
  ? T
  : T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends {}
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;